import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import Navbar from '@/components/Navbar';
import AddAttributeDialog from '@/components/AddAttributeDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import SchemaGroupFilter from '@/components/SchemaGroupFilter';
import {
  Layers, FolderOpen, Plus, Pencil, Trash2, ChevronDown, ChevronRight,
  FileText, Sparkles, Loader2, Code, Calendar, Hash, Type, ToggleLeft, Search, Repeat
} from 'lucide-react';
import { toast } from 'sonner';

const TYPE_ICONS = { string: Type, code: Code, date: Calendar, number: Hash, integer: Hash, float: Hash, boolean: ToggleLeft, text: FileText };
const TYPE_COLORS = { string: 'bg-zinc-100 text-zinc-600', code: 'bg-blue-50 text-blue-700', date: 'bg-amber-50 text-amber-700', number: 'bg-emerald-50 text-emerald-700', integer: 'bg-emerald-50 text-emerald-700', float: 'bg-teal-50 text-teal-700', boolean: 'bg-purple-50 text-purple-700', text: 'bg-rose-50 text-rose-600' };

function EntityCard({ entity, canEdit, onEdit, onDelete, onAddGroup, onEditGroup, onDeleteGroup, onDocClick, onAddAttribute, navigate }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="border border-[#E5E7EB]" data-testid={`entity-card-${entity.entity_id}`}>
      <div className="flex items-center gap-3 px-4 py-3 bg-[#F9FAFB]">
        <button onClick={() => setExpanded(p => !p)} className="text-[#4B5563]" data-testid={`entity-expand-${entity.entity_id}`}>
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
        <Layers className="w-4 h-4 text-[#002FA7]" strokeWidth={1.5} />
        <div className="flex-1">
          <span className="text-sm font-semibold text-[#0A0A0A]" style={{ fontFamily: 'Chivo, sans-serif' }}>{entity.name}</span>
          {entity.is_repeating && <Badge variant="outline" className="ml-2 text-[9px] h-4 px-1.5 rounded-none border-amber-300 text-amber-700 bg-amber-50">REPEATING</Badge>}
        </div>
        <span className="text-[10px] text-[#9CA3AF]">{entity.group_count} groups, {entity.attribute_count} attrs</span>
        <Button variant="ghost" size="sm" className="h-7 px-2 rounded-none text-[#002FA7]" onClick={() => onDocClick(entity)} data-testid={`doc-entity-${entity.entity_id}`}>
          <FileText className="w-3 h-3 mr-1" /><span className="text-[11px]">Docs</span>
        </Button>
        {canEdit && (
          <>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 rounded-none" onClick={() => onEdit(entity)} data-testid={`edit-entity-${entity.entity_id}`}><Pencil className="w-3 h-3" /></Button>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 rounded-none text-red-500 hover:text-red-700" onClick={() => onDelete(entity)} data-testid={`delete-entity-${entity.entity_id}`}><Trash2 className="w-3 h-3" /></Button>
          </>
        )}
      </div>
      {expanded && (
        <div className="px-4 py-3 border-t border-[#E5E7EB]">
          {entity.description && <p className="text-xs text-[#4B5563] mb-3">{entity.description}</p>}
          {/* Groups with their attributes */}
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#9CA3AF]">Groups & Attributes</span>
            {canEdit && (
              <div className="flex gap-1">
                <Button variant="outline" size="sm" className="h-6 text-[10px] rounded-none" onClick={() => onAddGroup(entity)} data-testid={`add-group-${entity.entity_id}`}><Plus className="w-3 h-3 mr-1" /> Group</Button>
                <Button variant="outline" size="sm" className="h-6 text-[10px] rounded-none text-[#002FA7] border-[#002FA7]/30" onClick={() => onAddAttribute(entity)} data-testid={`add-attr-${entity.entity_id}`}><Plus className="w-3 h-3 mr-1" /> Attribute</Button>
              </div>
            )}
          </div>
          {(entity.groups || []).map(g => (
            <div key={g.group_id} className="mb-1" data-testid={`group-item-${g.group_id}`}>
              <div className="flex items-center gap-2 px-3 py-2 border border-[#E5E7EB] bg-[#F9FAFB]">
                <FolderOpen className="w-3.5 h-3.5 text-[#4B5563]" strokeWidth={1.5} />
                <span className="text-xs font-semibold text-[#4B5563] flex-1 uppercase tracking-[0.05em]">{g.name}</span>
                {g.is_repeating && <span className="text-[9px] px-1 bg-amber-50 text-amber-600">REP</span>}
                <span className="text-[10px] text-[#9CA3AF]">{g.attribute_count} attrs</span>
                {canEdit && (
                  <>
                    <Button variant="ghost" size="sm" className="h-5 w-5 p-0 rounded-none" onClick={() => onEditGroup(g)}><Pencil className="w-2.5 h-2.5" /></Button>
                    <Button variant="ghost" size="sm" className="h-5 w-5 p-0 rounded-none text-red-500" onClick={() => onDeleteGroup(g)}><Trash2 className="w-2.5 h-2.5" /></Button>
                  </>
                )}
              </div>
              {/* Attributes in this group */}
              {(entity.attributes || []).filter(a => a.group_id === g.group_id).map(a => {
                const Icon = TYPE_ICONS[a.data_type] || Type;
                return (
                  <button key={a.attribute_id} onClick={() => navigate(`/attributes/${a.attribute_id}`)} className="w-full flex items-center gap-2 px-3 py-1.5 pl-8 border-x border-b border-[#E5E7EB] hover:bg-[#F3F4F6] text-left" data-testid={`attr-item-${a.attribute_id}`}>
                    <Icon className="w-3 h-3 text-[#9CA3AF]" strokeWidth={1.5} />
                    <span className="text-sm text-[#0A0A0A] flex-1 truncate">{a.name}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 font-medium ${TYPE_COLORS[a.data_type] || 'bg-zinc-100 text-zinc-600'}`} style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{a.data_type}</span>
                  </button>
                );
              })}
            </div>
          ))}
          {/* Ungrouped attributes */}
          {(entity.attributes || []).filter(a => !a.group_id).length > 0 && (
            <div className="mt-1">
              <div className="px-3 py-1 text-[10px] text-[#9CA3AF] uppercase tracking-[0.1em]">Ungrouped</div>
              {(entity.attributes || []).filter(a => !a.group_id).map(a => {
                const Icon = TYPE_ICONS[a.data_type] || Type;
                return (
                  <button key={a.attribute_id} onClick={() => navigate(`/attributes/${a.attribute_id}`)} className="w-full flex items-center gap-2 px-3 py-1.5 pl-8 border border-[#E5E7EB] mb-px hover:bg-[#F3F4F6] text-left" data-testid={`attr-item-${a.attribute_id}`}>
                    <Icon className="w-3 h-3 text-[#9CA3AF]" strokeWidth={1.5} />
                    <span className="text-sm text-[#0A0A0A] flex-1 truncate">{a.name}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 font-medium ${TYPE_COLORS[a.data_type] || 'bg-zinc-100 text-zinc-600'}`} style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{a.data_type}</span>
                  </button>
                );
              })}
            </div>
          )}
          {(!entity.groups || entity.groups.length === 0) && (entity.attributes || []).length === 0 && (
            <div className="text-xs text-[#9CA3AF] py-3 text-center border border-dashed border-[#E5E7EB]">No groups or attributes</div>
          )}
        </div>
      )}
    </div>
  );
}

export default function EntitiesPage() {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const canEdit = hasPermission('edit_elements');
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editingGroup, setEditingGroup] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', is_repeating: false, sort_order: 0 });
  const [groupForm, setGroupForm] = useState({ entity_id: '', name: '', description: '', is_repeating: false, sort_order: 0 });
  const [suggesting, setSuggesting] = useState(false);
  const [addAttrOpen, setAddAttrOpen] = useState(false);
  const [addAttrEntity, setAddAttrEntity] = useState(null);
  const [search, setSearch] = useState('');
  const [filterRepeating, setFilterRepeating] = useState('all');
  const [filterScope, setFilterScope] = useState(null); // {type, id, label}

  const fetchEntities = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (filterRepeating === 'yes') params.set('is_repeating', 'true');
      if (filterRepeating === 'no') params.set('is_repeating', 'false');
      if (filterScope?.type === 'group') params.set('group_id', filterScope.id);
      if (filterScope?.type === 'schema') params.set('schema_id', filterScope.id);
      const res = await api.get(`/entities?${params}`);
      const enriched = await Promise.all(res.data.map(async e => {
        const detail = await api.get(`/entities/${e.entity_id}`);
        return { ...e, groups: detail.data.groups || [], attributes: detail.data.attributes || [] };
      }));
      setEntities(enriched);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { setLoading(true); fetchEntities(); }, [search, filterRepeating, filterScope]);

  const openCreate = () => { setEditing(null); setForm({ name: '', description: '', is_repeating: false, sort_order: 0 }); setDialogOpen(true); };
  const openEdit = (e) => { setEditing(e); setForm({ name: e.name, description: e.description || '', is_repeating: e.is_repeating, sort_order: e.sort_order || 0 }); setDialogOpen(true); };
  const openAddGroup = (entity) => { setEditingGroup(null); setGroupForm({ entity_id: entity.entity_id, name: '', description: '', is_repeating: false, sort_order: 0 }); setGroupDialogOpen(true); };
  const openEditGroup = (g) => { setEditingGroup(g); setGroupForm({ entity_id: g.entity_id, name: g.name, description: g.description || '', is_repeating: g.is_repeating, sort_order: g.sort_order || 0 }); setGroupDialogOpen(true); };
  const openAddAttribute = (entity) => { setAddAttrEntity(entity); setAddAttrOpen(true); };

  const handleAISuggestEntity = async () => {
    if (!form.name.trim()) { toast.error('Enter a name first'); return; }
    setSuggesting(true);
    try {
      const r = await api.post('/entities/ai-suggest', { name: form.name.trim() });
      setForm(p => ({ ...p, description: r.data.description || p.description, is_repeating: r.data.is_repeating ?? p.is_repeating }));
      toast.success('AI suggestions applied');
    } catch { toast.error('AI suggestion failed'); }
    finally { setSuggesting(false); }
  };

  const handleSave = async () => {
    try {
      if (editing) { await api.put(`/entities/${editing.entity_id}`, form); toast.success('Entity updated'); }
      else { await api.post('/entities', form); toast.success('Entity created'); }
      setDialogOpen(false); fetchEntities();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleDelete = async (entity) => {
    if (!window.confirm(`Delete "${entity.name}"?`)) return;
    try { await api.delete(`/entities/${entity.entity_id}`); toast.success('Deleted'); fetchEntities(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleSaveGroup = async () => {
    try {
      if (editingGroup) { await api.put(`/groups/${editingGroup.group_id}`, { name: groupForm.name, description: groupForm.description, is_repeating: groupForm.is_repeating, sort_order: groupForm.sort_order }); toast.success('Group updated'); }
      else { await api.post('/groups', groupForm); toast.success('Group created'); }
      setGroupDialogOpen(false); fetchEntities();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleDeleteGroup = async (g) => {
    if (!window.confirm(`Delete group "${g.name}"?`)) return;
    try { await api.delete(`/groups/${g.group_id}`); toast.success('Deleted'); fetchEntities(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  if (loading) {
    return (<div className="min-h-screen bg-white"><Navbar /><div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-zinc-300 border-t-zinc-900 rounded-full animate-spin" /></div></div>);
  }

  return (
    <div className="min-h-screen bg-white" data-testid="entities-page">
      <Navbar />
      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="flex items-end justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-[#0A0A0A]" style={{ fontFamily: 'Chivo, sans-serif' }}>Entities & Groups</h1>
            <p className="text-sm text-[#4B5563] mt-1">{entities.length} entities defined</p>
          </div>
          {canEdit && <Button onClick={openCreate} className="rounded-none bg-[#0A0A0A] text-white hover:bg-[#171717] h-8 text-xs" data-testid="create-entity-btn"><Plus className="w-3.5 h-3.5 mr-1.5" /> New Entity</Button>}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 mb-4 flex-wrap" data-testid="entity-filters">
          <div className="relative max-w-xs flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#9CA3AF]" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search entities..." className="pl-8 h-8 text-sm rounded-none" data-testid="entity-search" />
          </div>
          <div className="flex items-center gap-1">
            <Repeat className="w-3 h-3 text-[#9CA3AF]" />
            {['all', 'yes', 'no'].map(v => (
              <button key={v} onClick={() => setFilterRepeating(v)} className={`text-[10px] px-2 py-1 transition-colors ${filterRepeating === v ? 'bg-[#0A0A0A] text-white' : 'bg-zinc-100 text-[#4B5563]'}`} data-testid={`filter-repeating-${v}`}>
                {v === 'all' ? 'All' : v === 'yes' ? 'Repeating' : 'Non-repeating'}
              </button>
            ))}
          </div>
          <SchemaGroupFilter value={filterScope} onChange={setFilterScope} />
        </div>

        <div className="space-y-px" data-testid="entities-list">
          {entities.map(entity => (
            <EntityCard key={entity.entity_id} entity={entity} canEdit={canEdit} onEdit={openEdit} onDelete={handleDelete} onAddGroup={openAddGroup} onEditGroup={openEditGroup} onDeleteGroup={handleDeleteGroup} onDocClick={e => navigate(`/entities/${e.entity_id}/doc`)} onAddAttribute={openAddAttribute} navigate={navigate} />
          ))}
        </div>

        {/* Entity Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="rounded-none" data-testid="entity-dialog">
            <DialogHeader><DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>{editing ? 'Edit Entity' : 'New Entity'}</DialogTitle><DialogDescription className="sr-only">Create or edit entity</DialogDescription></DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <Label className="text-xs font-semibold text-[#4B5563]">Name</Label>
                <div className="flex gap-2 mt-1">
                  <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="rounded-none flex-1" data-testid="entity-name-input" placeholder="e.g., Lab Results" />
                  {!editing && (
                    <Button variant="outline" size="sm" onClick={handleAISuggestEntity} disabled={suggesting || !form.name.trim()} className="rounded-none h-9 px-3 shrink-0" data-testid="entity-ai-suggest-btn">
                      {suggesting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}<span className="ml-1.5 text-xs">AI</span>
                    </Button>
                  )}
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#4B5563]">Description</Label>
                <Textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} className="rounded-none mt-1" data-testid="entity-desc-input" rows={2} />
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={form.is_repeating} onCheckedChange={v => setForm(p => ({ ...p, is_repeating: v }))} data-testid="entity-repeating-switch" />
                <Label className="text-sm text-[#4B5563]">Repeating entity (multiple instances per patient)</Label>
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#4B5563]">Sort Order</Label>
                <Input type="number" value={form.sort_order} onChange={e => setForm(p => ({ ...p, sort_order: parseInt(e.target.value) || 0 }))} className="rounded-none mt-1 w-24" data-testid="entity-order-input" />
              </div>
              <Button onClick={handleSave} className="w-full rounded-none bg-[#002FA7] hover:bg-[#002585]" data-testid="save-entity-btn">{editing ? 'Update' : 'Create'} Entity</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Group Dialog */}
        <Dialog open={groupDialogOpen} onOpenChange={setGroupDialogOpen}>
          <DialogContent className="rounded-none" data-testid="group-dialog">
            <DialogHeader><DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>{editingGroup ? 'Edit Group' : 'New Group'}</DialogTitle><DialogDescription className="sr-only">Create or edit group</DialogDescription></DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <Label className="text-xs font-semibold text-[#4B5563]">Name</Label>
                <Input value={groupForm.name} onChange={e => setGroupForm(p => ({ ...p, name: e.target.value }))} className="rounded-none mt-1" data-testid="group-name-input" placeholder="e.g., Dates" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#4B5563]">Description</Label>
                <Textarea value={groupForm.description} onChange={e => setGroupForm(p => ({ ...p, description: e.target.value }))} className="rounded-none mt-1" data-testid="group-desc-input" rows={2} />
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={groupForm.is_repeating} onCheckedChange={v => setGroupForm(p => ({ ...p, is_repeating: v }))} data-testid="group-repeating-switch" />
                <Label className="text-sm text-[#4B5563]">Repeating group</Label>
              </div>
              <Button onClick={handleSaveGroup} className="w-full rounded-none bg-[#002FA7] hover:bg-[#002585]" data-testid="save-group-btn">{editingGroup ? 'Update' : 'Create'} Group</Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Add Attribute Dialog (reused component) */}
        <AddAttributeDialog
          open={addAttrOpen}
          onOpenChange={setAddAttrOpen}
          entities={entities}
          schemaId={null}
          schemaName=""
          onCreated={() => fetchEntities()}
        />
      </div>
    </div>
  );
}
